// Responsive sheet. On `<md` renders bottom-anchored with a drag
// handle and sits above the on-screen keyboard. On `≥md` renders as
// a centered dialog. Reuses the existing base-ui Dialog primitives
// so focus trapping, escape-to-close, and overlay behaviour are
// consistent with ExportDialog / UploadConfirmDialog.

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { cn } from "@/lib/utils";

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: BottomSheetProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-50 bg-black/10 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            // mobile: bottom sheet
            "fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-border bg-bg-raised p-4 pb-6 shadow-2xl outline-none",
            "data-open:animate-in data-open:slide-in-from-bottom data-closed:animate-out data-closed:slide-out-to-bottom",
            // desktop: centered dialog
            "md:inset-auto md:left-1/2 md:top-1/2 md:w-full md:max-w-md md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-xl md:border md:p-5 md:pb-5",
            "md:data-open:slide-in-from-top-2 md:data-closed:slide-out-to-top-2",
            className,
          )}
        >
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-border md:hidden" />
          <DialogPrimitive.Title className="text-base font-semibold">
            {title}
          </DialogPrimitive.Title>
          {description && (
            <DialogPrimitive.Description className="mt-1 text-sm text-text-soft">
              {description}
            </DialogPrimitive.Description>
          )}
          <div className="mt-4">{children}</div>
          {footer && <div className="mt-5 flex gap-2">{footer}</div>}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
